import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// This app runs React 17, where `useId` does not exist — importing it yields
// undefined and throws on first render. A module-level counter gives each tip a
// stable unique id for aria-describedby without needing React 18.
let tipSequence = 0;

const GAP = 8;
const EDGE = 12;

/**
 * Place the tip against its anchor in viewport coordinates.
 *
 * Above the anchor by default, flipped below when there is no room, and pulled
 * back from either edge so a tip on the last column never runs off screen.
 *
 * @param {DOMRect} anchor
 * @param {DOMRect} tip
 * @param {"start"|"center"} align
 * @returns {{ left: number, top: number }}
 */
export function placeTip(anchor, tip, align = "start") {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = align === "center"
    ? anchor.left + anchor.width / 2 - tip.width / 2
    : anchor.left;

  left = Math.min(left, viewportWidth - tip.width - EDGE);
  left = Math.max(EDGE, left);

  let top = anchor.top - tip.height - GAP;
  if (top < EDGE) {
    top = anchor.bottom + GAP;
  }
  top = Math.min(top, viewportHeight - tip.height - EDGE);
  top = Math.max(EDGE, top);

  return { left, top };
}

/**
 * A small explanatory popover for terms the dashboard invents.
 *
 * This page names things Operations cannot be expected to know from the label
 * alone — "Delivery" vs "Import", "feed", "backfill". The native `title`
 * attribute is a poor fit: it waits about a second, cannot be styled, cannot
 * hold a second line, and never appears for keyboard users at all.
 *
 * Rendered through a portal onto `document.body` and positioned with
 * `position: fixed`. It used to be absolutely positioned inside the anchor,
 * which looked like a z-index problem but was really clipping: the detail panel
 * is an `.integrations-card` with `overflow: hidden`, and table headers sit
 * inside `.integrations-table-scroll`, whose `overflow-x: auto` makes the
 * vertical axis clip too (per CSS Overflow, a non-visible axis forces the other
 * to compute to `auto`). A tip opening upward from a header was cut off by the
 * container edge. On the body, no ancestor can clip it.
 *
 * Opens on hover and on focus, closes on blur, on Escape, and on scroll, so it
 * is reachable by keyboard and never left floating away from its anchor.
 *
 * @param {Object} props
 * @param {string} props.summary - One line, the plain-language meaning.
 * @param {string} [props.detail] - Optional second paragraph: why it matters.
 * @param {React.ReactNode} props.children - The element the tip explains.
 * @param {"start"|"center"} [props.align="start"]
 */
export function InfoTip({ summary, detail, children, align = "start" }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const tipIdRef = useRef(null);
  if (tipIdRef.current === null) {
    tipSequence += 1;
    tipIdRef.current = `integrations-tip-${tipSequence}`;
  }
  const tipId = tipIdRef.current;

  const reposition = useCallback(() => {
    if (!anchorRef.current || !tipRef.current) {
      return;
    }

    setPosition(
      placeTip(
        anchorRef.current.getBoundingClientRect(),
        tipRef.current.getBoundingClientRect(),
        align,
      ),
    );
  }, [align]);

  // Measured after the tip is in the DOM, because placement needs its real
  // size. It renders offscreen for that one frame rather than in the wrong spot.
  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    reposition();

    // Any scroll moves the anchor, and a fixed tip would not follow it.
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, reposition]);

  if (!summary) {
    return children;
  }

  return (
    <span
      className="integrations-tip-anchor"
      ref={anchorRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
        }
      }}
    >
      <span tabIndex={0} aria-describedby={open ? tipId : undefined}>
        {children}
      </span>
      {open
        ? ReactDOM.createPortal(
            <span
              className="integrations-tip"
              role="tooltip"
              id={tipId}
              ref={tipRef}
              style={
                position
                  ? { left: position.left, top: position.top }
                  : { left: 0, top: -9999 }
              }
            >
              <span className="integrations-tip-summary">{summary}</span>
              {detail ? (
                <span className="integrations-tip-detail">{detail}</span>
              ) : null}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * A standalone "?" affordance for labels that need explaining but have no
 * element of their own worth making hoverable.
 */
export function InfoDot({ summary, detail, align = "start" }) {
  return (
    <InfoTip summary={summary} detail={detail} align={align}>
      <span className="integrations-tip-dot" aria-label="What is this?">
        ?
      </span>
    </InfoTip>
  );
}
